import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bottle, Clock, Sparkles, Heart, MessageCircle, Trophy, Compass, ShoppingBag, User, Loader2, Info } from "lucide-react";

// lucide non ha "Bottle"; fallback icon
import { Wine as BottleIcon } from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
const CONTEST_LABEL = {
  none: _t("Nessun contest"),
  first_reaction: _t("🎁 Prima reazione vince"),
  first_comment: _t("💬 Primo commento vince"),
  riddle: _t("🧩 Enigma da risolvere"),
};

function Card({ children, className = "", ...p }) {
  return <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 ${className}`} {...p}>{children}</div>;
}

function MiabPublicCard({ m, onReact, onOpenDetail }) {
  return (
    <Card data-testid={`miab-${m.id}`} className="hover:border-white/20 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
            {m.content_purpose === "commercial" ? _t("MIAB · Commerciale") : _t("MIAB · Personale")}
          </div>
          <h3 className="text-xl text-white font-display">{m.title}</h3>
          <div className="text-xs text-white/50 mt-1">
            <User className="inline w-3 h-3 mr-1" />{m.author_display_name}
            <span className="mx-2">·</span>
            <Clock className="inline w-3 h-3 mr-1" />{_t("Aperto il")}{" "}{new Date(m.opened_at).toLocaleDateString(_locale())}
          </div>
        </div>
        {m.contest_type !== "none" && (
          <div className="text-[11px] text-amber-300 border border-amber-400/30 bg-amber-500/5 rounded-full px-3 py-1">
            {CONTEST_LABEL[m.contest_type]}
          </div>
        )}
      </div>
      <div className="text-white/90 whitespace-pre-wrap leading-relaxed mt-4">{m.message}</div>
      {m.riddle_question && (
        <div className="mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-400/20">
          <div className="text-xs text-amber-200 mb-1">{_t("🧩 Enigma")}</div>
          <div className="text-white/95">{m.riddle_question}</div>
        </div>
      )}
      <div className="flex items-center justify-between mt-4">
        <button data-testid={`miab-react-${m.id}`} onClick={() => onReact(m, "ti_sento")} className="btn-ghost !py-1 !px-3 text-sm inline-flex items-center gap-1">
          <Heart className="w-4 h-4 text-rose-400" />{" "}{_t("Ti sento")}</button>
        <button data-testid={`miab-open-${m.id}`} onClick={() => onOpenDetail(m)} className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1">
          <MessageCircle className="w-4 h-4" />{" "}{_t("Commenti & Contest")}</button>
      </div>
    </Card>
  );
}

function MiabDetail({ miab, onClose }) {
  const [comments, setComments] = useState([]);
  const [contest, setContest] = useState(null);
  const [text, setText] = useState("");
  const [riddleAnswer, setRiddleAnswer] = useState("");

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get(`/miab/${miab.id}/comments`),
        api.get(`/miab/${miab.id}/contest-status`),
      ]);
      setComments(c.data.comments);
      setContest(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [miab.id]);

  const sendComment = async () => {
    if (text.trim().length < 1) return;
    try {
      const { data } = await api.post(`/miab/${miab.id}/comments`, { text: text.trim() });
      setComments((c) => [...c, data]);
      setText("");
      if (data.contest_won_now) {
        toast.success(_t("🏆 Hai vinto il contest del primo commento!"));
        load();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitRiddle = async () => {
    if (!riddleAnswer.trim()) return;
    try {
      const { data } = await api.post(`/miab/${miab.id}/riddle-answer`, { answer: riddleAnswer.trim() });
      if (data.ok) toast.success(_t("🏆 Risposta corretta! Hai vinto il contest."));
      else if (data.reason === "already_won") toast.info(_t("Contest già vinto da {0}", data.winner_display_name || "un altro Dreamer"));
      else toast.error(_t("Risposta errata, riprova."));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="miab-detail-modal" className="max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0F1A2E] border border-white/10 p-6 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl text-white font-display">{miab.title}</h2>
            <div className="text-xs text-white/50 mt-1">{_t("di {0}", miab.author_display_name)}</div>
          </div>
          <button data-testid="miab-detail-close" onClick={onClose} className="btn-ghost !p-2">✕</button>
        </div>
        <div className="text-white/90 whitespace-pre-wrap leading-relaxed">{miab.message}</div>

        {contest && contest.contest_type !== "none" && (
          <div className={`p-3 rounded-xl border ${contest.resolved ? "border-emerald-400/30 bg-emerald-500/5" : "border-amber-400/30 bg-amber-500/5"}`}>
            <div className="text-xs text-amber-200 mb-1">{CONTEST_LABEL[contest.contest_type]}</div>
            {contest.resolved ? (
              <div className="text-emerald-200 text-sm">{_t("🏆 Vinto da")}{" "}<strong>{contest.winner_display_name}</strong>{" "}{_t("il")}{" "}{new Date(contest.won_at).toLocaleString(_locale())}
                {contest.prize_note && <div className="text-white/60 text-xs mt-1">{_t("Premio: {0}", contest.prize_note)}</div>}
              </div>
            ) : contest.contest_type === "riddle" && miab.riddle_question ? (
              <div className="space-y-2">
                <div className="text-white/90">{miab.riddle_question}</div>
                <div className="flex gap-2">
                  <input data-testid="riddle-answer-input" value={riddleAnswer} onChange={(e)=>setRiddleAnswer(e.target.value)}
                    className="flex-1 bg-black/20 border border-white/10 rounded-full px-4 py-2 text-sm text-white" placeholder={_t("La tua risposta…")} />
                  <button data-testid="riddle-submit" onClick={submitRiddle} className="btn-primary !py-2 !px-4">{_t("Rispondi")}</button>
                </div>
              </div>
            ) : (
              <div className="text-white/60 text-sm">{_t("Contest ancora aperto. Il primo a interagire vince!")}</div>
            )}
          </div>
        )}

        <div className="pt-3 border-t border-white/5">
          <div className="text-white/70 text-sm mb-2">{_t("{0} commenti", comments.length)}</div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {comments.map((c) => (
              <div key={c.id} className="text-sm bg-white/[0.03] rounded-xl px-3 py-2">
                <div className="flex justify-between">
                  <span className="text-white/70 text-xs font-semibold">{c.author_display_name}</span>
                  <span className="text-white/30 text-[10px]">{new Date(c.created_at).toLocaleString(_locale())}</span>
                </div>
                <div className="text-white/90 mt-1 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <input data-testid="miab-comment-input" value={text} onChange={(e)=>setText(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&sendComment()}
                   placeholder={_t("Aggiungi un commento…")} className="flex-1 bg-black/20 border border-white/10 rounded-full px-4 py-2 text-sm text-white" />
            <button data-testid="miab-comment-send" onClick={sendComment} className="btn-primary !py-2 !px-4">{_t("Invia")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MiabExplore() {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/miab/feed/public");
      setFeed(data.miab);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const react = async (m, reaction) => {
    try {
      const { data } = await api.post(`/miab/${m.id}/reactions`, null, { params: { reaction } });
      if (data.contest_won_now) toast.success(_t("🏆 Hai vinto il contest della prima reazione!"));
      else toast.success(_t("Reazione registrata"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="space-y-8">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-2">
            <h1 className="font-display text-4xl sm:text-5xl text-white flex items-center gap-3">
              <BottleIcon className="w-10 h-10 text-cyan-400" />{" "}{_t("Message in a Bottle")}</h1>
            <p className="text-white/60 text-sm max-w-2xl">{_t("Capsule pubbliche che si aprono per tutta la community a una data futura. Reagisci, commenta e prova a vincere i contest temporali.")}</p>
          </div>
          <button data-testid="miab-new-btn" onClick={() => nav("/miab/nuovo")} className="btn-primary">
            <Sparkles className="w-4 h-4 mr-2 inline" />{" "}{_t("Lancia il tuo MIAB")}</button>
        </header>

        {loading ? (
          <div className="text-white/50 text-sm">{_t("Carico i messaggi pubblici…")}</div>
        ) : feed.length === 0 ? (
          <Card data-testid="miab-empty" className="text-center py-10">
            <BottleIcon className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <div className="text-white/60">{_t("Nessun MIAB pubblico ancora aperto.")}</div>
            <div className="text-white/40 text-xs mt-1">{_t("Lancia tu il primo, si aprirà tra almeno un anno.")}</div>
          </Card>
        ) : (
          <div className="grid gap-4">
            {feed.map((m) => (
              <MiabPublicCard key={m.id} m={m} onReact={react} onOpenDetail={setDetail} />
            ))}
          </div>
        )}

        {detail && <MiabDetail miab={detail} onClose={() => setDetail(null)} />}
      </div>
    </AppLayout>
  );
}
